import React, { useEffect, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Pagination,
    TextField,
    Button,
} from '@mui/material';
import axios, { isCancel } from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import MenuItem from '@mui/material/MenuItem';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';


const Dashboard = ({ }) => {
    const { userId } = useAuth();
    const navigate = useNavigate();
    const { role } = useAuth();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({
        employeeName: '',
        totalBookings: 0,
        totalAmount: 0,
        couponCodes:[],
    });
    const [bookings, setBookings] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const [representatives, setRepresentatives] = useState([])
    const [selectedRepId, setSelectedRepId] = useState(null);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const recordsPerPage = 50;
    const API_URL = process.env.REACT_APP_API_URL;

    const [loadingSeconds, setLoadingSeconds] = useState(0);

    useEffect(() => {
            let timer;
          
            if (loading) {
              setLoadingSeconds(0); // reset timer
              timer = setInterval(() => {
                setLoadingSeconds(prev => prev + 1);
              }, 1000);
            } else {
              clearInterval(timer);
              setLoadingSeconds(0); // optional reset after done
            }
          
            return () => clearInterval(timer); // cleanup on unmount or loading = false
          }, [loading]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const params = {
                user_id: selectedRepId || userId,
                limit: recordsPerPage,
                offset: (currentPage - 1) * recordsPerPage,
            };

            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const res = await axios.get(`${API_URL}/api/sales/`, { params });
            const data = res.data;

            setSummary({
                employeeId: data.employee_code,
                employeeName: data.employee_name,
                employeeSurname: data.employee_surname,
                totalBookings: data.totalBookings,
                totalAmount: data.totalAmount,
                couponCodes: data.coupon_codes,
            });

            setBookings(data.bookings);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [userId, currentPage]); // fetch on mount and when page changes

    useEffect(() => {
    setLoading(true); // Start loading

    axios.get(`${API_URL}/api/anthra-representatives/`)
        .then((res) => {
            setRepresentatives(res.data);
        })
        .catch((err) => {
            console.error('Error fetching representatives:', err);
        })
        .finally(() => {
            setLoading(false); // Stop loading regardless of success or error
        });
}, []);


    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleFilter = () => {
        setCurrentPage(1); // Reset to first page when applying filter
        fetchDashboardData();
    };

    const totalPages = Math.ceil(summary.totalBookings / recordsPerPage);

    const handleClick = () => {
    navigate('/admin-create-accounts');
  };

    return (
        <div>
            <Header1 />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6, bgcolor: 'black', minHeight: '80vh', color: 'white' }}>
        
            <Typography variant="h4" fontWeight="bold" gutterBottom color="white">
                Sales Dashboard
            </Typography>

            {/* 🔍 Date Filter Section */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                    <Button 
      variant="contained" 
      color="primary" 
      onClick={handleClick}
    >
      Create Accounts
    </Button>
                    <DatePicker
                        label="Start Date"
                        value={startDate ? new Date(startDate) : null}
                        onChange={(newValue) =>
                            setStartDate(newValue ? newValue.toISOString().slice(0, 10) : '')
                        }
                        slotProps={{
                            textField: {
                                variant: 'outlined',
                                sx: { input: { color: 'white' }, label: { color: 'gray' } },
                            },
                        }}
                    />

                    <DatePicker
                        label="End Date"
                        value={endDate ? new Date(endDate) : null}
                        onChange={(newValue) =>
                            setEndDate(newValue ? newValue.toISOString().slice(0, 10) : '')
                        }
                        slotProps={{
                            textField: {
                                variant: 'outlined',
                                sx: { input: { color: 'white' }, label: { color: 'gray' } },
                            },
                        }}
                    />

                    {(role === 'admin' || role === 'anthra-sales-executive') && (
                        <TextField
                            select
                            label="Representative"
                            value={selectedRepId}
                            onChange={(e) => setSelectedRepId(e.target.value)}
                            variant="outlined"
                            sx={{ minWidth: 200, color: 'white', label: { color: 'gray' }, '& .MuiInputBase-input': { color: 'white' } }}
                        >
                            <MenuItem value={userId}>Me</MenuItem>
                            {representatives.map((rep) => (
                                <MenuItem key={rep.user_id} value={rep.user_id}>
                                    {rep.user_name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}


                    <Button variant="contained" onClick={handleFilter}>
                        Apply Filter
                    </Button>
                </Box>
            </LocalizationProvider>



            {/* Summary Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* ... summary boxes unchanged ... */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={4} sx={{ p: 3, bgcolor: '#1e1e1e', color: 'white' }}>
                        <Typography variant="subtitle2" color="gray">ID</Typography>
                        <Typography variant="h6">{summary.employeeId || '--'}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={4} sx={{ p: 3, bgcolor: '#1e1e1e', color: 'white' }}>
                        <Typography variant="subtitle2" color="gray">Name</Typography>
                        <Typography variant="h6">
                            {(summary.employeeName && summary.employeeSurname)
                                ? `${summary.employeeName} ${summary.employeeSurname}`
                                : summary.employeeName || summary.employeeSurname || '--'}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={4} sx={{ p: 3, bgcolor: '#1e1e1e', color: 'white' }}>
                        <Typography variant="subtitle2" color="gray">Total Bookings</Typography>
                        <Typography variant="h6">{summary.totalBookings}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={4} sx={{ p: 3, bgcolor: '#1e1e1e', color: 'white' }}>
                        <Typography variant="subtitle2" color="gray">Total Amount (₹)</Typography>
                        <Typography variant="h6">₹ {summary.totalAmount}</Typography>
                    </Paper>
                </Grid>

                 {/* Coupon Codes (NEW) */}
    <Grid item xs={12} sm={6} md={3}>
        <Paper elevation={4} sx={{ p: 3, bgcolor: '#1e1e1e', color: 'white' }}>
            <Typography variant="subtitle2" color="gray">Coupon Codes</Typography>

            {summary.couponCodes && summary.couponCodes.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {summary.couponCodes.map((code, index) => (
                        <Box
                            key={index}
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                bgcolor: '#292929',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                border: '1px solid #444',
                            }}
                        >
                            {code}
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography variant="h6">--</Typography>
            )}
        </Paper>
    </Grid>
            </Grid>

            {/* Table Section */}
            {loading ? (
                <Box
                                                sx={{
                                                  position: 'fixed',
                                                  top: 0,
                                                  left: 0,
                                                  width: '100vw',
                                                  height: '100vh',
                                                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                  zIndex: 2000,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  backdropFilter: 'blur(3px)',
                                                }}
                                              >
                                                <Box sx={{ textAlign: 'center' }}>
                                                  <CircularProgress
                                      size={60}
                                      thickness={6}
                                      sx={{
                                        color: 'transparent',
                                        '& .MuiCircularProgress-circle': {
                                          stroke: 'url(#better-gradient)',
                                        },
                                      }}
                                    />
                                    
                                    <svg width="0" height="0">
                                      <defs>
                                        <linearGradient id="better-gradient" x1="1" y1="0" x2="0" y2="1">
                                          <stop offset="0%" stopColor="#f30b96ff" />     {/* pinkish-purple */}
                                          <stop offset="50%" stopColor="#221ce0ff" />     {/* deep lavender */}
                                          <stop offset="100%" stopColor="#0eacebff" />    {/* bright sky blue */}
                                        </linearGradient>
                                      </defs>
                                    </svg>
                                    
                                                  <Typography
                                                    variant="h6"
                                                    sx={{
                                                      mt: 2,
                                                      color: '#fff',
                                                      textShadow: '0 1px 3px rgba(0,0,0,0.7)',
                                                    }}
                                                  >
                                                    Loading Sales Details... ({loadingSeconds} sec)
                                                  </Typography>
                                                </Box>
                                              </Box>
            ) : (
                <>
                    <TableContainer component={Paper} elevation={3} sx={{ bgcolor: '#121212', color: 'white' }}>
                        <Table>
                            <TableHead sx={{ bgcolor: '#2c2c2c' }}>
                                <TableRow>
                                    {['#', 'Coupon Code', 'Session Name', 'Price (₹)', 'Booking Date', 'Booking Time', "isCancelled"].map((header) => (
                                        <TableCell key={header} sx={{ color: 'white', fontWeight: 'bold' }}>
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bookings.map((row, index) => (
                                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#1a1a1a' } }}>
                                        <TableCell sx={{ color: 'white' }}>{(currentPage - 1) * recordsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ color: 'white' }}>{row.couponCode || 'N/A'}</TableCell>
                                        <TableCell sx={{ color: 'white' }}>{row.sessionName || '--'}</TableCell>
                                        <TableCell sx={{ color: 'white' }}>₹ {row.price}</TableCell>
                                        {/* <TableCell sx={{ color: 'white' }}>{row.userName || '--'}</TableCell>
                                        <TableCell sx={{ color: 'white' }}>{row.contact || '--'}</TableCell>
                                        <TableCell sx={{ color: 'white' }}>{row.email || '--'}</TableCell> */}
                                        <TableCell sx={{ color: 'white' }}>{row.bookingDate || '--'}</TableCell>
                                        <TableCell sx={{ color: 'white' }}>{row.bookingTime || '--'}</TableCell>
                                        <TableCell sx={{ color: 'white' }}>{row.isCancelled}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Box display="flex" justifyContent="center" mt={4}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            sx={{
                                '& .MuiPaginationItem-root': { color: 'white' },
                            }}
                        />
                    </Box>
                </>
            )}
        </Container>
        <Footer2/>

        </div>
    );
};

export default Dashboard;
